import { useParams, Link, Navigate } from "react-router-dom";
import {
  CheckCircle, WhatsappLogo, ArrowLeft, PaperPlaneTilt,
  DownloadSimple, Star,
} from "@phosphor-icons/react";
import { getProduct, relatedProducts, localizeProduct } from "@/lib/products";
import { openWhatsApp } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { SmartImage } from "@/components/SmartImage";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const base = getProduct(slug);

  if (!base) return <Navigate to="/products" replace />;

  const product = localizeProduct(base, lang);
  const related = relatedProducts(base);
  const enquire = () =>
    openWhatsApp(`Hello RK Jain,\nI am interested in your ${base.name}. Please share more details.`);

  // A "clean" product has a dedicated cropped image that is DIFFERENT from the brochure URL.
  // For legacy items where image === brochure, we still treat the hero image as brochure-style.
  const hasCleanImage = base.brochureImage
    ? base.image !== base.brochureImage
    : !base.isBrochure;
  const heroContain = !hasCleanImage; // legacy brochure hero renders contained on white
  // Only show a dedicated "Product Brochure" section when we have a real brochure image
  // that is DIFFERENT from the hero image (i.e., clean cutout + separate brochure).
  const fullBrochure =
    base.brochureImage && base.brochureImage !== base.image ? base.brochureImage : null;

  return (
    <div data-testid="product-detail-page">
      {/* Hero banner */}
      <section className="relative flex min-h-[46vh] items-end overflow-hidden bg-forest pt-28">
        {heroContain ? (
          <div className="absolute inset-0 bg-gradient-to-br from-forest via-emerald to-forest" />
        ) : (
          <SmartImage src={base.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/55 to-ink/25" />
        <div className="hero-grain absolute inset-0" />
        <div className="container-x relative z-10 pb-12">
          <Link to="/products" className="mb-5 inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold" data-testid="back-to-products">
            <ArrowLeft size={16} weight="bold" /> {t("common.allProducts")}
          </Link>
          <p className="overline mb-3">{t(`categories.${base.category}`)}</p>
          <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight text-white md:text-6xl">
            {product.name}
          </h1>
        </div>
      </section>

      {/* Overview + image */}
      <section className="container-x py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal className={`relative aspect-[4/3] overflow-hidden rounded-2xl shadow-sm ring-1 ring-hairline ${heroContain ? "bg-white" : "bg-champagne"}`}>
            <SmartImage src={base.image} alt={product.name} className={`h-full w-full ${heroContain ? "object-contain p-3" : "object-contain p-6"}`} />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline mb-4">{t("common.productOverview")}</p>
            {(base.subcategory || base.model) && (
              <div className="mb-4 flex flex-wrap gap-2">
                {base.subcategory && (
                  <span className="rounded-full bg-champagne px-3 py-1 text-xs font-semibold text-forest">{base.subcategory}</span>
                )}
                {base.model && (
                  <span className="rounded-full border border-hairline px-3 py-1 text-xs font-semibold text-subtle">{t("productExtra.model")}: {base.model}</span>
                )}
              </div>
            )}
            <p className="text-lg leading-relaxed text-ink/75">{product.overview}</p>
            {!base.specs && (
              <div className="mt-8 rounded-xl border border-hairline bg-champagne p-5 text-sm text-subtle">
                {t("common.specNote")}
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={enquire} className="btn-gold" data-testid="detail-enquire-button">
                <WhatsappLogo size={20} weight="fill" /> {t("common.enquireNow")}
              </button>
              <Link to="/enquiry" className="btn-outline-forest" data-testid="detail-enquiry-form-link">
                <PaperPlaneTilt size={18} weight="fill" /> {t("common.requestQuote")}
              </Link>
              {base.brochure && (
                <a href={base.brochure} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-hairline bg-surface px-8 py-4 font-semibold text-forest shadow-sm transition-colors hover:bg-champagne"
                  data-testid="download-brochure">
                  <DownloadSimple size={18} weight="bold" /> {t("productExtra.downloadBrochure")}
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features + Applications */}
      <section className="bg-champagne py-16 md:py-24">
        <div className="container-x grid gap-14 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-forest">{t("common.keyFeatures")}</h2>
            <ul className="mt-8 space-y-4">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-forest" />
                  <span className="text-ink/75">{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-forest">{t("common.applications")}</h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {base.applications.map((a) => (
                <span key={a}
                  className="inline-flex items-center gap-2 rounded-lg border border-hairline bg-surface px-4 py-2.5 text-sm font-medium text-forest shadow-sm">
                  {t(`apps.names.${a}`)}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Technical Specifications */}
      {base.specs && (
        <section className="container-x py-16 md:py-24" data-testid="product-specs">
          <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <h2 className="font-heading text-3xl font-extrabold tracking-tight text-forest">
                {t("productExtra.specifications")}
              </h2>
              <div className="mt-8 overflow-hidden rounded-2xl border border-hairline">
                <table className="w-full text-left text-sm">
                  <tbody>
                    {base.specs.map(([k, v], i) => (
                      <tr key={i} className={i % 2 ? "bg-surface" : "bg-champagne/60"}>
                        <th className="w-2/5 px-5 py-3 align-top font-semibold text-forest">{k}</th>
                        <td className="px-5 py-3 text-ink/75">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {base.note && (
                  <p className="border-t border-hairline bg-champagne/40 px-5 py-3 text-xs italic text-subtle">{base.note}</p>
                )}
              </div>
            </div>

            <div>
              {base.sizes && (
                <>
                  <h3 className="font-heading text-xl font-bold text-forest">{t("productExtra.availableSizes")}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {base.sizes.map((s) => (
                      <span key={s} className="rounded-lg border border-hairline bg-surface px-3 py-2 text-sm font-medium text-forest shadow-sm">{s}</span>
                    ))}
                  </div>
                </>
              )}
              {base.advantages && (
                <>
                  <h3 className="mt-8 font-heading text-xl font-bold text-forest">{t("productExtra.advantages")}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {base.advantages.map((a) => (
                      <li key={a} className="flex items-center gap-2.5 text-ink/75">
                        <Star size={16} weight="fill" className="shrink-0 text-gold" /> {a}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Product Brochure — full original brochure image at the bottom */}
      {fullBrochure && (
        <section className="container-x py-16 md:py-24" data-testid="product-brochure-section">
          <div className="mb-8 flex flex-col items-start gap-3">
            <p className="overline">{t("productExtra.productBrochure")}</p>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-forest md:text-4xl">
              {t("productExtra.productBrochure")}
            </h2>
            <p className="max-w-2xl text-ink/70">{t("productExtra.brochureCaption")}</p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-hairline bg-white shadow-sm">
            <a href={fullBrochure} target="_blank" rel="noopener noreferrer" data-testid="brochure-open-full">
              <SmartImage
                src={fullBrochure}
                alt={`${product.name} — full brochure`}
                className="h-auto w-full object-contain"
              />
            </a>
          </div>
          <div className="mt-6">
            <a href={fullBrochure} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-hairline bg-surface px-6 py-3 text-sm font-semibold text-forest shadow-sm transition-colors hover:bg-champagne"
              data-testid="brochure-download">
              <DownloadSimple size={18} weight="bold" /> {t("productExtra.downloadBrochure")}
            </a>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-champagne py-16 md:py-24">
          <div className="container-x">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-forest">{t("common.relatedProducts")}</h2>
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Big CTA */}
      <section className="bg-sand py-16 md:py-20">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-forest px-8 py-14 text-center shadow-xl shadow-forest/15 md:px-16">
            <div className="hero-grain absolute inset-0 opacity-40" />
            <div className="relative flex flex-col items-center gap-6">
              <h2 className="max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-sand md:text-4xl">
                {t("common.interestedIn", { name: product.name })}
              </h2>
              <button
                onClick={enquire}
                className="inline-flex items-center gap-2 rounded-xl bg-gold px-8 py-4 font-semibold text-forest transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
                data-testid="detail-bottom-enquire"
              >
                <WhatsappLogo size={22} weight="fill" /> {t("common.enquireNow")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
